import { useEffect, useState } from 'react'
import {Star, Search, Building2, Briefcase, ChevronLeft, ChevronRight, TrendingUp, BarChart2, Award, } from 'lucide-react'
import AdminLayout from '@/components/AdminLayout'
import { lumi } from '@/lib/lumi'
import { EVALUATION_ASPECTS } from '@/lib/evaluationAspects'

const PAGE_SIZE = 15

interface EvalRow {
  _id: string
  companyName?: string
  collaboratorName?: string
  companyUserId?: string
  collaboratorUserId?: string
  period?: string
  average?: number
  area?: string
  comments?: string
  createdAt?: string
}

function StarScore({ value }: { value: number }) {
  const color =
    value >= 8 ? 'text-emerald-600 bg-emerald-50' :
    value >= 6 ? 'text-amber-600 bg-amber-50' :
    'text-red-500 bg-red-50'
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-bold ${color}`}>
      <Star size={11} fill="currentColor" />
      {value?.toFixed(1) ?? '—'}
    </span>
  )
}

function AdminEvaluaciones() {
  const [evaluations, setEvaluations] = useState<EvalRow[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(0)
  const [globalAvg, setGlobalAvg] = useState(0)
  const [topCompany, setTopCompany] = useState<string>('')
  const [topColaborador, setTopColaborador] = useState<string>('')

  useEffect(() => {
    const fetchEvals = async () => {
      setLoading(true)
      try {
        const filter = search
          ? {
              $or: [
                { companyName: { $regex: search, $options: 'i' } },
                { collaboratorName: { $regex: search, $options: 'i' } },
                { area: { $regex: search, $options: 'i' } },
                { period: { $regex: search, $options: 'i' } },
              ],
            }
          : {}
        const res = await lumi.entities.evaluations.list({
          filter,
          sort: { createdAt: -1 },
          limit: PAGE_SIZE,
          skip: page * PAGE_SIZE,
        })
        setEvaluations(res.list as EvalRow[])
        setTotal(res.total ?? res.list.length)

        // KPIs globales en primera carga
        if (page === 0 && !search) {
          const allRes = await lumi.entities.evaluations.list({ limit: 500 })
          const all = allRes.list as EvalRow[]
          if (all.length > 0) {
            const avg = all.reduce((s, e) => s + (e.average ?? 0), 0) / all.length
            setGlobalAvg(avg)
            // Top empresa (mayor promedio)
            const byCompany: Record<string, { name: string; sum: number; count: number }> = {}
            all.forEach((e) => {
              const k = e.companyUserId ?? ''
              if (!byCompany[k]) byCompany[k] = { name: e.companyName ?? '—', sum: 0, count: 0 }
              byCompany[k].sum += e.average ?? 0
              byCompany[k].count++
            })
            const bestCompany = Object.values(byCompany).sort((a, b) => b.sum / b.count - a.sum / a.count)[0]
            setTopCompany(bestCompany?.name ?? '—')
            // Top colaborador
            const byColaborador: Record<string, { name: string; sum: number; count: number }> = {}
            all.forEach((e) => {
              const k = e.collaboratorUserId ?? ''
              if (!byColaborador[k]) byColaborador[k] = { name: e.collaboratorName ?? '—', sum: 0, count: 0 }
              byColaborador[k].sum += e.average ?? 0
              byColaborador[k].count++
            })
            const bestColaborador = Object.values(byColaborador).sort((a, b) => b.sum / b.count - a.sum / a.count)[0]
            setTopColaborador(bestColaborador?.name ?? '—')
          }
        }
      } catch (e) {
        console.error('[AdminEvaluaciones] error:', e)
      } finally {
        setLoading(false)
      }
    }
    fetchEvals()
  }, [search, page])

  const totalPages = Math.ceil(total / PAGE_SIZE)

  const kpis = [
    { label: 'Total evaluaciones', value: total, icon: BarChart2, color: 'text-violet-600', bg: 'bg-violet-50' },
    { label: 'Promedio global', value: globalAvg > 0 ? globalAvg.toFixed(2) : '—', icon: Star, color: 'text-amber-600', bg: 'bg-amber-50' },
    { label: 'Empresa más valorada', value: topCompany || '—', icon: Building2, color: 'text-blue-600', bg: 'bg-blue-50', small: true },
    { label: 'Colaborador más valorado', value: topColaborador || '—', icon: Award, color: 'text-emerald-600', bg: 'bg-emerald-50', small: true },
  ]

  return (
    <AdminLayout title="Evaluaciones" subtitle="Supervisa todas las evaluaciones de desempeño registradas en Beeznz Work">
          {/* KPIs */}
          <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
            {kpis.map(({ label, value, icon: Icon, color, bg, small }) => (
              <div key={label} className="rounded-xl border border-slate-200 bg-white p-5">
                <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${bg}`}>
                  <Icon size={20} className={color} />
                </div>
                <p className={`mt-3 font-bold text-slate-900 ${small ? 'text-sm leading-tight' : 'text-2xl'}`}>
                  {loading ? '—' : value}
                </p>
                <p className="mt-0.5 text-xs text-slate-500">{label}</p>
              </div>
            ))}
          </div>

          {/* Aspectos evaluados */}
          <div className="mb-6 rounded-xl border border-slate-200 bg-white p-5">
            <div className="mb-3 flex items-center gap-2">
              <TrendingUp size={16} className="text-amber-500" />
              <h2 className="text-sm font-semibold text-slate-900">Aspectos evaluados en la plataforma</h2>
            </div>
            <div className="flex flex-wrap gap-2">
              {EVALUATION_ASPECTS.map((a) => (
                <span
                  key={a.key}
                  className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-medium text-slate-600"
                >
                  {a.label}
                </span>
              ))}
            </div>
          </div>

          {/* Buscador */}
          <div className="mb-5 relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(0) }}
              placeholder="Buscar por empresa, colaborador, área o periodo…"
              className="w-full max-w-md rounded-lg border border-slate-200 bg-white py-2.5 pl-9 pr-4 text-sm outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100"
            />
          </div>

          {/* Tabla */}
          {loading ? (
            <div className="space-y-2">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="h-14 animate-pulse rounded-xl bg-slate-100" />
              ))}
            </div>
          ) : evaluations.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-slate-200 py-16 text-center">
              <Star size={32} className="mb-3 text-slate-300" />
              <p className="font-semibold text-slate-600">Sin evaluaciones registradas</p>
              <p className="text-sm text-slate-400">Aún no hay evaluaciones en la plataforma.</p>
            </div>
          ) : (
            <>
              <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
                <table className="w-full text-sm">
                  <thead className="border-b border-slate-100 bg-slate-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">Empresa</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">Colaborador</th>
                      <th className="hidden px-4 py-3 text-left text-xs font-semibold text-slate-500 sm:table-cell">Periodo</th>
                      <th className="hidden px-4 py-3 text-left text-xs font-semibold text-slate-500 md:table-cell">Área</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">Promedio</th>
                      <th className="hidden px-4 py-3 text-left text-xs font-semibold text-slate-500 lg:table-cell">Fecha</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {evaluations.map((ev) => (
                      <tr key={ev._id} className="hover:bg-slate-50">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-slate-900 text-[10px] font-bold text-amber-400">
                              {(ev.companyName ?? 'E').charAt(0).toUpperCase()}
                            </div>
                            <span className="text-xs font-medium text-slate-900 truncate max-w-[120px]">
                              {ev.companyName ?? '—'}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-xs text-slate-700 truncate max-w-[120px] block">
                            {ev.collaboratorName ?? '—'}
                          </span>
                        </td>
                        <td className="hidden px-4 py-3 text-xs text-slate-500 sm:table-cell">
                          {ev.period ?? '—'}
                        </td>
                        <td className="hidden px-4 py-3 text-xs text-slate-500 md:table-cell">
                          {ev.area ?? '—'}
                        </td>
                        <td className="px-4 py-3">
                          {ev.average != null ? <StarScore value={ev.average} /> : <span className="text-xs text-slate-400">—</span>}
                        </td>
                        <td className="hidden px-4 py-3 text-xs text-slate-400 lg:table-cell">
                          {ev.createdAt?.slice(0, 10) ?? '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 ? (
                <div className="mt-4 flex items-center justify-between text-sm text-slate-500">
                  <p>Página {page + 1} de {totalPages} · {total} evaluaciones</p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setPage((p) => Math.max(0, p - 1))}
                      disabled={page === 0}
                      className="flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium transition hover:bg-slate-50 disabled:opacity-40"
                    >
                      <ChevronLeft size={14} /> Anterior
                    </button>
                    <button
                      onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                      disabled={page >= totalPages - 1}
                      className="flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium transition hover:bg-slate-50 disabled:opacity-40"
                    >
                      Siguiente <ChevronRight size={14} />
                    </button>
                  </div>
                </div>
              ) : null}
            </>
          )}
    </AdminLayout>
  )
}

export default AdminEvaluaciones
